/**
 * Who can read an uploaded file. Values match the backend's `accessScope` vocabulary
 * one-for-one so there is a single spelling across the stack — the builder-facing
 * wording lives in FILE_VISIBILITY_OPTIONS below, not in these names.
 *
 * - USER: only the user who uploaded it
 * - APPLICATION: users of the app it was uploaded from
 * - ALL_USERS: any authenticated user of the tenant
 * - PUBLIC: anyone, including unauthenticated callers
 */
export enum FileVisibility {
  PUBLIC = 'PUBLIC',
  USER = 'USER',
  ALL_USERS = 'ALL_USERS',
  APPLICATION = 'APPLICATION',

  /**
   * @deprecated Use ALL_USERS. Persisted in blocks saved before visibility adopted the
   * backend spelling; still resolved by FILE_VISIBILITY_TO_ACCESS_SCOPE.
   */
  RESTRICTED = 'RESTRICTED',
  /**
   * @deprecated Use USER. Persisted in blocks saved before visibility adopted the
   * backend spelling; still resolved by FILE_VISIBILITY_TO_ACCESS_SCOPE.
   */
  ONLY_ME = 'ONLY_ME',
}

/**
 * The single source of truth for how visibility is worded in every builder surface.
 * Ordered most-restrictive to least, so the dropdown reads as a spectrum.
 *
 * The labels deliberately do not match the enum names — "Restricted" reads as
 * "locked down" to a configurator, which is what tenant-wide access means when the
 * neighbouring option is Public. The captions carry the precise meaning.
 *
 * Shaped as JSON Schema `oneOf` entries: RJSF turns each into an enum option and
 * SelectTemplate lifts `caption` onto it, so a plain `oneOf` renders the labelled,
 * captioned dropdown with no custom widget (same as TreeView's dragNDropMode).
 *
 * Deprecated values are absent on purpose — they stay readable, but nothing new
 * should be able to pick them.
 */
export const FILE_VISIBILITY_OPTIONS = [
  {
    const: FileVisibility.USER,
    title: 'Only me',
    caption: 'Accessible to the uploading user only',
  },
  {
    const: FileVisibility.APPLICATION,
    title: 'Only this app',
    caption: 'Accessible to users of this app',
  },
  {
    const: FileVisibility.ALL_USERS,
    title: 'Restricted',
    caption: 'Accessible to all authenticated users',
  },
  {
    const: FileVisibility.PUBLIC,
    title: 'Public',
    caption: 'Accessible to anyone',
  },
];

/**
 * Where a file lands when nobody has said otherwise — no block-level choice and no
 * app-level default. Matches what uploads did before visibility was configurable, so
 * adopting the setting never re-scopes files that already exist.
 */
export const DEFAULT_FILE_VISIBILITY = FileVisibility.ALL_USERS;

/**
 * What a stored value should read as in the builder.
 *
 * Deprecated spellings are deliberately absent from FILE_VISIBILITY_OPTIONS, so a block
 * still holding one has no matching option and its dropdown renders blank — the setting
 * looks unset even though the file is scoped. Run persisted values through here in the
 * form-data transformer so the panel shows what the block actually does.
 *
 * `allowPublicAccess` is the boolean the dropdown replaced; a block that had it on was
 * public, so it maps to PUBLIC when no visibility was ever chosen.
 */
export const normalizeFileVisibility = (
  fileVisibility?: FileVisibility,
  allowPublicAccess?: boolean,
): FileVisibility | undefined => {
  switch (fileVisibility) {
    case FileVisibility.ONLY_ME:
      return FileVisibility.USER;
    case FileVisibility.RESTRICTED:
      return FileVisibility.ALL_USERS;
    case undefined:
      return allowPublicAccess ? FileVisibility.PUBLIC : undefined;
    default:
      return fileVisibility;
  }
};
